import { useInfiniteQuery } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries } from '@round/api';
import {
    useBrandsQueriesByIds,
    useCampaignQueriesByIds,
    useProjectsQueriesByIds,
    usePurchaseOrder,
    useTeamsQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';
import uniq from 'lodash/uniq';

type Config = {
    enabled?: boolean;
};

export default function usePurchaseOrderAllocations(purchaseOrderId: string | null | undefined, config?: Config) {
    const query = usePurchaseOrder(purchaseOrderId, { staleTime: 0, ...config });
    const allocationsData = useAllocations(purchaseOrderId, config);

    return {
        purchaseOrder: query.data,
        isPurchaseOrderLoading: query.isLoading,
        errorMessage: query.errorMessage,
        ...allocationsData,
        refresh: () => {
            allocationsData.refresh();
            query.refetch();
        },
    };
}

const allocationsOptions = creatorbaseQueries.purchaseOrderLines.options.infinite;
const allocationsUtils = creatorbaseQueries.purchaseOrderLines.utils.list;

export type PurchaseOrderAllocation = creatorbase.PurchaseOrderLine & {
    campaign: creatorbase.Campaign | null;
    brand: creatorbase.Brand | null;
    project: creatorbase.Project | null;
    team: creatorbase.Team | null;
};

function useAllocations(purchaseOrderId: string | null | undefined, config?: Config) {
    const query = useInfiniteQuery({
        ...allocationsOptions({ purchase_order_id: purchaseOrderId?.toString() }),
        enabled: !!purchaseOrderId && config?.enabled,
        staleTime: 0,
    });

    const campaignIds = query.data?.pages.map((page) => uniq(page.results.map((l) => l.campaign_id))) ?? [];
    const campaignsResults = useCampaignQueriesByIds(campaignIds);

    const projectIds = campaignsResults.map((result) => result?.data?.results.map((c) => c.project_id) ?? []);
    const projectsResults = useProjectsQueriesByIds(projectIds);

    const brandIds = projectsResults.map((result) => result?.data?.results.map((p) => p.brand_id) ?? []);
    const brandsResults = useBrandsQueriesByIds(brandIds);

    const teamIds = projectsResults.map((result) => result?.data?.results.map((p) => p.team_id) ?? []);
    const teamsResults = useTeamsQueriesByIds(teamIds);

    const allocations =
        query.data?.pages.flatMap((page, index) =>
            page.results.map<PurchaseOrderAllocation>((l) => {
                const campaign = campaignsResults[index]?.data?.results.find((c) => c.id === l.campaign_id) ?? null;
                const project =
                    projectsResults[index]?.data?.results.find((p) => p.id === campaign?.project_id) ?? null;
                const brand = brandsResults[index]?.data?.results.find((b) => b.id === project?.brand_id) ?? null;
                const team = teamsResults[index]?.data?.results.find((t) => t.id === project?.team_id) ?? null;

                return {
                    ...l,
                    campaign,
                    project,
                    brand,
                    team,
                };
            })
        ) ?? [];

    const allocationsErrorMessage = !query.error
        ? null
        : allocationsUtils.isExpectedErrorResponse(query.error)
          ? query.error.response.data.detail
          : query.error.message;

    const hasSupplementaryErrors =
        campaignsResults.some((result) => result?.error) ||
        projectsResults.some((result) => result?.error) ||
        brandsResults.some((result) => result?.error) ||
        teamsResults.some((result) => result?.error);

    return {
        allocations,
        areAllocationsLoading:
            query.isFetching ||
            campaignsResults.some((result) => result?.isFetching) ||
            projectsResults.some((result) => result?.isFetching) ||
            brandsResults.some((result) => result?.isFetching) ||
            teamsResults.some((result) => result?.isFetching),

        allocationsErrorMessage: allocationsErrorMessage,
        hasSupplementaryErrors,
        refresh: () => {
            query.refetch();
        },
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
    };
}
